import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import JobTabStyling from '../JobTabStyling';
import { Breadcrumbs } from '../Breadcrumb';
import { NavTabBar } from '../Tabs';
import Headline3 from '../Typography/Heading/Headline3';
import Body1 from '../Typography/Body/Body1';
import { markdownStyling } from '../InnerHtmlStyling';
import breakpoints from '../Theme/breakpoints';
import Hr from '../Hr';
import grey from '../Palettes/grey';
import colors from '../Theme/colors';

const Container = styled.div`
  max-width: 970px;
  margin: 0 auto;
  width: 100%;
`;

const BreadcrumbsContainer = styled.div`
  display: grid;
  grid-template-areas: "breadcrumbs postJob";
  padding: 0 20px 20px;
`;

const JobsGrid = styled.div<{
  hasImage?: boolean;
  hasAds?: boolean;
}>`
  display: grid;
  margin: ${({ hasImage }) => (hasImage ? '0' : '0 0 20px')};
  padding: 0 20px;
  position: relative;

  ${({ hasAds }) => (hasAds ? `
    grid-template-areas:
      "header"
      "content"
      "ads"
      "footer";
    grid-template-columns: 1fr;
  ` : `
    grid-template-areas:
      "header"
      "content"
      "footer";
    grid-template-columns: 1fr;
  `)}
`;

const JobsGridCell = styled.div`
  padding-bottom: 16px;
  background-color: white;
  @media (min-width: ${breakpoints.xl}px) {
    padding: 0 16px 8px;
  }
`;

const WfbAdsGridCell = styled.div`
  grid-area: ads;
  display: none;
  padding-bottom: 16px;

  @media (min-width: ${breakpoints.lg}px) {
    display: block;
    min-height: 307px;
  }
`;

export const SearchCell = styled.div<{
  hideIntroTextOnMobile?: boolean;
}>`
  width: 100%;
  min-height: 248px;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas:
    "searchInput"
    "locationInput"
    "locationBadges"
    "categoryBadges"
    "additionalOptions";
  grid-template-rows: auto auto 50px 50px auto;

  @media (min-width: ${breakpoints.sm}px) {
    min-height: 216px;
    grid-template-columns: 1fr 1fr;
    grid-template-areas:
      "searchInput locationInput"
      "locationBadges locationBadges"
      "categoryBadges categoryBadges"
      "additionalOptions additionalOptions";
    grid-template-rows: auto 50px 50px auto;
    gap: 0 24px;
  }

  @media (min-width: ${breakpoints.lg}px) {
    min-height: 200px;
  }

  margin: ${(properties) => (
    properties.hideIntroTextOnMobile ? '1rem 0 0' : '0')};
  align-self: center;
  background-color: white;

  @media (min-width: ${breakpoints.xl}px) {
    padding: 0 16px 16px;
  }
`;

const titleOnImageStyle = css`
  font-size: 1.5rem;
  color: black;
  background-color: rgba(239,239,239);
  line-height: 1em;
  padding: 1.5rem 0;
`;

const Title = styled(Headline3)<{
  hasImage?: boolean;
}>`
  text-align: center;

  && {
    ${({ hasImage }) => hasImage && titleOnImageStyle}
  }

`;

const LogoContainer = styled.div`
  /* stylelint-disable-next-line selector-type-no-unknown */
  && progressive-img, img {
    height: 100%;
    max-width: 220px;
    --img-width: 100%;
    --img-height: 220px;
  }

  @media (min-width: ${breakpoints.sm}px) {
    margin: 24px;
    width: 220px;
    height: 220px;
    min-height: 14rem;
  }
`;

const introductionContainerOnImageStyle = css`
  grid-template-columns: 1fr;
  justify-items: center;

  @media (min-width: ${breakpoints.sm}px) {
    grid-template-columns: auto 1fr;
    margin: 0 auto;
    background-color: ${colors.white};
    padding: 24px;
    min-height: 8vw;
  }
`;

const IntroductionContainer = styled.div<{
  hasTabs?: boolean;
  hasImage?: boolean;
  hideIntroTextOnMobile?: boolean;
}>`
  display: grid;
  gap: 0;
  justify-items: center;
  align-items: ${({ hasTabs }) => (hasTabs ? 'start' : 'center')};
  ${({ hasImage }) => hasImage && introductionContainerOnImageStyle}
  margin: ${(properties) => (
    properties.hideIntroTextOnMobile
      ? '16px 0 0'
      : '16px 0'
  )}
`;

const introductionOnImageStyle = css`
  @media (min-width: ${breakpoints.sm}px) {
    text-align: initial;
    line-height: 1.5em;
    margin: 24px 0;
    width: 100%;
  }
`;

const Introduction = styled.div`
  overflow: hidden;
  width: 100%;
`;

const IntroductionBody = styled(Body1)<{
  hasImage?: boolean;
  hideIntroTextOnMobile?: boolean;
}>`
  text-align: center;
  white-space: pre-wrap;
  ${({ hasImage }) => hasImage && introductionOnImageStyle}

  display: ${(properties) => (
      properties.hideIntroTextOnMobile ? 'none' : 'block')
  };

  @media (min-width: ${breakpoints.sm}px) {
    display: block;
  }

  a {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const Description = styled(JobTabStyling)<{
  hideIntroTextOnMobile?: boolean;
}>`
  ${({ theme }) => markdownStyling(theme.palette.primary.main)}
  ${({ theme }) => theme.typography?.body1}

  img {
    width: 100%;
  }

  iframe {
    background: ${colors.black};
    border: 0;
    width: 100%;
    min-height: 180px;

    @media (min-width: ${breakpoints.xssm}px) {
      min-height: 270px;
    }

    @media (min-width: ${breakpoints.sm}px) {
      min-height: 337.5px;
    }

    @media (min-width: ${breakpoints.md}px) {
      min-height: 472.5px;
    }

    @media (min-width: ${breakpoints.lg}px) {
      min-height: 523px;
    }

    @supports (aspect-ratio: 16/9) {
      /* stylelint-disable-next-line property-no-unknown */
      aspect-ratio: 16/9;
    }
  }

  h3 {
    margin: 0;
  }

  @media (min-width: ${breakpoints.sm}px) {
    h3 {
      margin: initial;
    }
  }

  margin: ${(properties) => (
    properties.hideIntroTextOnMobile ? '0' : '32px 0')};
`;

const EditButtonContainer = styled.div`
  text-align: center;
  margin: 0px 24px;

  button {
    border-radius: 0;
    height: 40px;
  }

  @media (min-width: ${breakpoints.sm}px) {
    text-align: right;
  }
`;

const breadcrumbsOnImageStyle = css`
  @media (min-width: ${breakpoints.sm}px) {
    && {
      padding: 0px;
    }
  }
`;

const JobsBreadcrumbs = styled(Breadcrumbs)<{
  hasImage?: boolean;
}>`
  height: 40px;
  margin: 2px 0 0 0;
  grid-area: breadcrumbs;

  ${({ hasImage }) => hasImage && breadcrumbsOnImageStyle}
`;

const PostJobButtonWrapper = styled.div`
  display: none;
  grid-area: postJob;

  @media (min-width: ${breakpoints.sm}px) {
    display: block;
    text-align: end;
  }
`;

const JobsTabContainer = styled.div`
  position: relative;
`;

export const JobsTabBar = styled(NavTabBar)`
  margin: 0 0 16px;
  .MuiTabs-flexContainer {
    border-bottom: 2px solid ${grey[325]};
  }

  .MuiTab-root {
    font-size: 0.85rem;
    letter-spacing: 0;
    flex-grow: 1;
    max-width: none;
  }
`;

const Fade = styled.div`
  display: inline;
  pointer-events: none;
  position: absolute;
  bottom: 0;
  right: -1px;
  height: 100%;
  width: 10%;
  background: linear-gradient(to right, rgba(255,255,255,0) 0%,
    rgba(255,255,255,1) 100%);

  @media (min-width: ${breakpoints.sm}px) {
    width: 5%;
  }
`;

const StyledHr = styled(Hr)<{
  hideIntroTextOnMobile?: boolean;
}>`
  display: ${(properties) => (
    properties.hideIntroTextOnMobile ? 'none' : 'block')};

  @media (min-width: ${breakpoints.sm}px) {
    display: block;
  }
`;

const HeaderArea = styled.div`
  grid-area: header;
  overflow: hidden;
  max-width: 100%;
`;

const ContentArea = styled.div`
  grid-area: content;
`;

const FooterArea = styled.div`
  grid-area: footer;
`;

const BannerWithTitle = styled.div<{
  hasImage?: boolean;
}>`
  ${({ hasImage }) => !hasImage && 'margin-top: 0rem;'}
`;

const jobsPropertyTypes = {
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.node,
  ).isRequired,
  title: PropTypes.node.isRequired,
  tabs: PropTypes.node,
  introduction: PropTypes.node.isRequired,
  description: PropTypes.node,
  profileEditButton: PropTypes.node,
  search: PropTypes.node,
  children: PropTypes.node,
  headerImage: PropTypes.node,
  logo: PropTypes.node,
  hideIntroTextOnMobile: PropTypes.bool,
  hasTabs: PropTypes.bool,
  wfbAds: PropTypes.node,
  postJobButton: PropTypes.node,
};

type JobsProperties =
  InferProps<typeof jobsPropertyTypes>;

const Jobs = ({
  breadcrumbs,
  title,
  tabs,
  introduction,
  description,
  profileEditButton,
  search,
  children,
  headerImage,
  logo,
  hideIntroTextOnMobile,
  hasTabs,
  wfbAds,
  postJobButton,
}: JobsProperties) => (
  <>
    {headerImage && (
      <BannerWithTitle hasImage={Boolean(headerImage)}>
        {headerImage}
        <Title tag="h1" hasImage={Boolean(headerImage)}>
          {title}
        </Title>
      </BannerWithTitle>
    )}
    <Container>
      {
        !headerImage
        && (
        <BreadcrumbsContainer>
          <JobsBreadcrumbs
            crumbs={breadcrumbs}
            hasImage={Boolean(headerImage)}
          />
          { postJobButton && (
            <PostJobButtonWrapper>
              { postJobButton }
            </PostJobButtonWrapper>
          )}
        </BreadcrumbsContainer>
        )
      }
      {!headerImage && (
        <BannerWithTitle hasImage={Boolean(headerImage)}>
          <Title tag="h1" hasImage={Boolean(headerImage)}>
            {title}
          </Title>
        </BannerWithTitle>
      )}
      <JobsGrid hasImage={Boolean(headerImage)} hasAds={Boolean(wfbAds)}>
        <HeaderArea>
          {
          headerImage
          && (
            <JobsBreadcrumbs
              crumbs={breadcrumbs}
              hasImage={Boolean(headerImage)}
            />
          )
        }
          <IntroductionContainer
            hasTabs={hasTabs ?? false}
            hasImage={Boolean(headerImage)}
            hideIntroTextOnMobile={hideIntroTextOnMobile ?? false}
          >
            {logo && (
            <LogoContainer>
              {logo}
            </LogoContainer>
            )}
            <Introduction>
              {tabs && (
              <JobsTabContainer>
                  {tabs}
                <Fade />
              </JobsTabContainer>
              )}
              <IntroductionBody
                tag="div"
                hasImage={Boolean(logo)}
                hideIntroTextOnMobile={hideIntroTextOnMobile ?? false}
              >
                {introduction}
              </IntroductionBody>
            </Introduction>
          </IntroductionContainer>
          {profileEditButton && (
            <EditButtonContainer>{profileEditButton}</EditButtonContainer>
          )}

        </HeaderArea>

        <ContentArea>
          {description && (
          <JobsGridCell>
            <StyledHr hideIntroTextOnMobile={hideIntroTextOnMobile ?? false} />
            <Description hideIntroTextOnMobile={hideIntroTextOnMobile ?? false}>
              {description}
            </Description>
          </JobsGridCell>
          )}
          {search && (
          <SearchCell hideIntroTextOnMobile={hideIntroTextOnMobile ?? false}>
            {search}
          </SearchCell>
          )}
        </ContentArea>

        <WfbAdsGridCell>
          {wfbAds}
        </WfbAdsGridCell>

        <FooterArea>
          {children}
        </FooterArea>
      </JobsGrid>
    </Container>
  </>
);

Jobs.propTypes = jobsPropertyTypes;

export default Jobs;
